import { TaskInternalExternal, TaskStatus } from '@gcv/shared';

import type { FilterListChild, FilterListItem } from 'ui';
import { getTaskStatusText } from 'util/format.util';
import type { TaskRow } from '../../tasks/tasks.model';
import { TaskRowType } from '../../tasks/tasks.model';

const FREQUENCIES = ['Weekly', 'Monthly', 'Quarterly', 'Semi-Annually', 'Annually', 'One-Time'];

export interface InitializedFilterOptions {
  taskDirection: TaskInternalExternal;
  /** Restored in place of the Open + In Progress default when returning from the questionnaire overview. */
  preSelectedFilters?: FilterListChild[];
}

/** One option per distinct cell value; an empty cell reads as '--' but filters under its own label. */
const getFilterChildren = (
  values: string[],
  parentValue: string,
  emptyOption?: { label: string; value: string }
): FilterListChild[] =>
  [...new Set(values)]
    .sort((a, b) => (a < b ? -1 : 1))
    .map((value) => ({
      label: value === '--' && emptyOption ? emptyOption.label : value,
      value: value === '--' && emptyOption ? emptyOption.value : value,
      parentValue
    }));

/** The filter options the tasks page offers, built from the rows it currently has loaded. */
export const getInitializedFilters = (
  tasks: TaskRow[],
  options: InitializedFilterOptions
): FilterListItem[] => {
  const { taskDirection, preSelectedFilters } = options;
  const createdBy = tasks.filter((t) => t.createdBy !== 'Auto Generated').map((t) => t.createdBy);
  const statusOption = (status: TaskStatus, selected?: boolean) => ({
    label: getTaskStatusText(status),
    value: status,
    parentValue: 'status',
    selected
  });

  const filters: FilterListItem[] =
    taskDirection === TaskInternalExternal.INTERNAL
      ? [
          {
            label: 'Status',
            value: 'status',
            children: [
              statusOption(TaskStatus.OPEN, !preSelectedFilters),
              statusOption(TaskStatus.PROCESSING, !preSelectedFilters),
              statusOption(TaskStatus.COMPLETE)
            ]
          },
          {
            label: 'Assignee',
            value: 'assignee',
            children: getFilterChildren(
              tasks.map((t) => t.assignee),
              'assignee',
              { label: 'None', value: 'assignee_none' }
            )
          },
          {
            label: 'Account',
            value: 'account',
            children: getFilterChildren(
              tasks.map((t) => t.account),
              'account',
              { label: 'None', value: 'account_none' }
            )
          },
          {
            label: 'Created By',
            value: 'createdBy',
            children: getFilterChildren(createdBy, 'createdBy', {
              label: 'Auto Created',
              value: 'autoCreated'
            })
          }
        ]
      : [
          {
            label: 'Status',
            value: 'status',
            children: [
              statusOption(TaskStatus.OPEN, !preSelectedFilters),
              statusOption(TaskStatus.PROCESSING, !preSelectedFilters),
              statusOption(TaskStatus.SUPPRESSED),
              statusOption(TaskStatus.COMPLETE)
            ]
          },
          {
            label: 'Type',
            value: 'type',
            children: [
              { label: 'Task', value: TaskRowType.Task, parentValue: 'type' },
              { label: 'Questionnaire', value: TaskRowType.Questionnaire, parentValue: 'type' },
              {
                label: 'Questionnaire Revision',
                value: TaskRowType.QuestionnaireRevision,
                parentValue: 'type'
              }
            ]
          },
          {
            label: 'Frequency',
            value: 'frequency',
            children: FREQUENCIES.map((frequency) => ({
              label: frequency,
              value: frequency,
              parentValue: 'frequency'
            }))
          },
          {
            label: 'Created By',
            value: 'createdBy',
            children: getFilterChildren(createdBy, 'createdBy')
          }
        ];

  preSelectedFilters?.forEach((preSelected) => {
    const child = filters
      .find((filter) => filter.value === preSelected.parentValue)
      ?.children.find((c) => c.value === preSelected.value);

    if (child) {
      child.selected = true;
    }
  });

  return filters;
};
